import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import Navigation from '../common/Navigation';
import TeamsPage from '../teams/TeamsPage';
import ActivityDashboard from '../activities/ActivityDashboard';
// RunsPage removed - using Daily Log instead
import GoalsPage from '../goals/GoalsPage';
import PointsPage from '../points/PointsPage';
import ProfilePage from '../profile/ProfilePage';
import PrizesPage from '../prizes/PrizesPage';
import ChallengesMissionsPage from '../challenges/ChallengesMissionsPage';
import AvatarShopPage from '../shop/AvatarShopPage';
// CoachAvatarShopPage now accessed via Teams page tabs

interface DashboardProps {
  userTeamId: string | null;
  onLogActivity?: () => void;
  onActivitySubmitted?: (navigateToProfile?: () => void) => void;
  navigateToPage?: string; // Prop to trigger navigation from parent
  activitiesRefreshTrigger?: number; // Trigger to refresh activities list
}

const Dashboard: React.FC<DashboardProps> = ({ userTeamId, onLogActivity, onActivitySubmitted, navigateToPage, activitiesRefreshTrigger }) => {
  const { user } = useAuth();
  
  // Set default page based on user role
  // Coaches/Head Coaches: Team Management (manage athletes and teams) unless useAthleteWorkflow is enabled
  // Athletes: Daily Log (primary action - data capture!)
  const getDefaultPage = () => {
    if (user?.role === 'athlete') {
      return 'activities';
    }
    // For coaches/head coaches, check if they prefer athlete workflow
    if ((user?.role === 'coach' || user?.role === 'headCoach') && user?.useAthleteWorkflow) {
      return 'activities';
    }
    // Default for coaches/head coaches is teams
    return 'teams';
  };
  console.log('🏠 Dashboard: User role:', user?.role, 'useAthleteWorkflow:', user?.useAthleteWorkflow, 'Default page would be:', getDefaultPage());
  const [currentPage, setCurrentPage] = useState(() => getDefaultPage());
  
  // Update current page when user role or workflow preference loads (in case user wasn't loaded when component mounted)
  // Only update if we're still on the initial default or on a non-default page that should change
  useEffect(() => {
    if (user && !navigateToPage) {
      const expectedPage = getDefaultPage();
      // Only auto-switch if current page is 'activities' or 'teams' (the defaults)
      // Don't switch if user is on profile, goals, etc.
      if ((currentPage === 'activities' || currentPage === 'teams') && currentPage !== expectedPage) {
        console.log('🏠 Dashboard: Updating page from', currentPage, 'to', expectedPage, 'based on user role:', user.role, 'useAthleteWorkflow:', user.useAthleteWorkflow);
        setCurrentPage(expectedPage);
      }
    }
  }, [user?.role, user?.useAthleteWorkflow]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Navigate to page when prop changes
  useEffect(() => {
    if (navigateToPage) {
      setCurrentPage(navigateToPage);
    }
  }, [navigateToPage]);
  const [pointsRefreshTrigger, setPointsRefreshTrigger] = useState(0);

  const handlePageChange = useCallback((page: string) => {
    setCurrentPage(page);
    
    // Trigger points refresh when navigating to points page
    if (page === 'points') {
      setPointsRefreshTrigger(prev => prev + 1);
    }
  }, []); // Empty dependency array since setCurrentPage and setPointsRefreshTrigger are stable

  const renderPage = () => {
    switch (currentPage) {
      case 'teams':
        return <TeamsPage />;
      case 'activities':
        return <ActivityDashboard 
          teamId={userTeamId || ''} 
          onLogActivity={onLogActivity}
          onActivitySubmitted={() => {
            // Keep user on activities page after logging activity
            // Activities list will refresh automatically
            // Call parent callback if provided
            if (onActivitySubmitted) {
              onActivitySubmitted(() => setCurrentPage('activities'));
            }
          }}
          refreshTrigger={activitiesRefreshTrigger}
          navigateToTeams={() => setCurrentPage('teams')}
        />;
      case 'goals':
        return <GoalsPage />;
      case 'points':
        return <PointsPage refreshTrigger={pointsRefreshTrigger} />;
      case 'profile':
        return <ProfilePage />;
      case 'challenges':
        return <ChallengesMissionsPage />;
      case 'shop':
        return <AvatarShopPage />;
      case 'prizes':
        return <PrizesPage />;
      case 'dashboard':
      default:
        return (
          <Container maxWidth="lg" sx={{ mt: 0 }}>
            <Grid container spacing={3}>
              <Grid size={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h5" component="h2" gutterBottom>
                      Welcome to Streaker
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      You are logged in as a {user?.role}. 
                      {(user?.role === 'coach' || user?.role === 'headCoach')
                        ? ' You can create teams and manage your athletes.' 
                        : ' You can log your runs and join teams.'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {(user?.role === 'coach' || user?.role === 'headCoach') && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" component="h3" gutterBottom>
                        Team Management
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Create and manage your teams, invite athletes, and track their progress.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              <Grid size={{ xs: 12, md: (user?.role === 'coach' || user?.role === 'headCoach') ? 6 : 12 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" component="h3" gutterBottom>
                      Run Logging
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(user?.role === 'coach' || user?.role === 'headCoach')
                        ? 'View and analyze your athletes\' run data.' 
                        : 'Log your daily runs and track your progress.'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: user?.role === 'coach' ? 6 : 12 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" component="h3" gutterBottom>
                      Goals & Targets
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user?.role === 'coach' 
                        ? 'Monitor your athletes\' goals and progress.' 
                        : 'Set personal running goals and track your achievements.'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

            </Grid>
          </Container>
        );
    }
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      <Navigation currentPage={currentPage} onPageChange={handlePageChange} />
      <Box component="main" sx={{ 
        flexGrow: 1, 
        // Account for AppBar height + safe area insets + comfortable spacing
        paddingTop: {
          xs: 'calc(56px + env(safe-area-inset-top, 32px) + 20px + 8px + 16px)', // Mobile: toolbar + safe area + top offset (20px) + AppBar padding + content padding
          sm: 'calc(64px + env(safe-area-inset-top, 32px) + 20px + 8px + 16px)', // Desktop: toolbar + safe area + top offset (20px) + AppBar padding + content padding
        },
        pb: 2,
        minHeight: {
          xs: 'calc(100vh - 56px - env(safe-area-inset-top, 32px) - 20px - 8px)',
          sm: 'calc(100vh - 64px - env(safe-area-inset-top, 32px) - 20px - 8px)',
        }
      }}>
        {renderPage()}
      </Box>
    </Box>
  );
};

export default Dashboard;
